/**
 * A folded, collapsible list of money lines grouped by a date (or any other
 * bucket), with per-group totals.
 *
 * Exists because a per-item settlement is unreadable raw: a payout pays PER
 * ORDER ITEM, so one real envelope is hundreds of rows carrying the same
 * grouped label. Two folds make it scannable — identical rows within a bucket
 * collapse into one with a count, and each bucket is a collapsed section headed
 * by its own totals. The caller owns both folds and the money formatting; this
 * primitive owns only the presentation, so a chef-facing and an admin-facing
 * screen can render the same shape without sharing app code.
 *
 * Deliberately structural: no currency, no domain types, no knowledge of what a
 * "line" is. Pass `formatAmount` and pre-folded groups.
 */

import { ChevronRight } from 'lucide-react'

import { cn } from '../cn'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './collapsible'

/** One folded row inside a group. */
export interface PayoutLineGroupRow {
  key: string
  /** Primary label — the thing being paid for. */
  name: string
  /** Secondary muted line under the name (a campus, a location). Omitted when null. */
  detail?: string | null
  /** Marker rendered ahead of the name, e.g. "Dry run". Omitted when null. */
  badge?: string | null
  /**
   * Units this row covers. `null` renders NOTHING rather than `×0` — a zero
   * beside a real amount reads as a fact, and an unresolved quantity is not one.
   */
  quantity?: number | null
  amountCents: number
}

/** One bucket of rows — typically a delivery date. */
export interface PayoutLineGroup {
  key: string
  /** Header text for the bucket, already formatted by the caller. */
  label: string
  rows: PayoutLineGroupRow[]
  /** Summed units across `rows`. Null suppresses the unit half of the caption. */
  quantity?: number | null
  amountCents: number
}

export interface PayoutLineGroupsProps {
  groups: readonly PayoutLineGroup[]
  /** Formats a cent amount for display, including currency. */
  formatAmount: (amountCents: number) => string
  /** Singular noun for a row. Default "item". */
  rowNoun?: string
  /** Plural noun for the quantity. Default "servings". */
  quantityNoun?: string
  /**
   * Force every group open. When omitted, groups start open only if there is
   * exactly one — with nothing to choose between, a collapsed section is a
   * click for its own sake.
   */
  defaultOpen?: boolean
  className?: string
}

function plural(count: number, noun: string): string {
  return `${count} ${count === 1 ? noun : `${noun}s`}`
}

export function PayoutLineGroups({
  groups,
  formatAmount,
  rowNoun = 'item',
  quantityNoun = 'servings',
  defaultOpen,
  className,
}: PayoutLineGroupsProps) {
  const openByDefault = defaultOpen ?? groups.length === 1
  return (
    <div className={cn('space-y-2', className)}>
      {groups.map((group) => (
        <Collapsible key={group.key} defaultOpen={openByDefault}>
          <div className="rounded-md border">
            <CollapsibleTrigger className="group flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted/40">
              <ChevronRight
                className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-90"
                aria-hidden
              />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium">{group.label}</span>
                <span className="block text-xs text-muted-foreground">
                  {plural(group.rows.length, rowNoun)}
                  {group.quantity !== null && group.quantity !== undefined
                    ? ` · ${group.quantity.toLocaleString()} ${quantityNoun}`
                    : ''}
                </span>
              </span>
              <span className="shrink-0 text-sm tabular-nums">{formatAmount(group.amountCents)}</span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ul className="divide-y border-t">
                {group.rows.map((row) => (
                  <li key={row.key} className="flex items-start justify-between gap-3 px-3 py-2 text-sm">
                    <span className="min-w-0 wrap-break-word">
                      {row.badge ? (
                        <span className="mr-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                          {row.badge}
                        </span>
                      ) : null}
                      {row.name}
                      {row.detail ? <span className="block text-xs text-muted-foreground">{row.detail}</span> : null}
                    </span>
                    <span className="flex shrink-0 items-baseline gap-3 tabular-nums">
                      {row.quantity !== null && row.quantity !== undefined ? (
                        <span className="text-muted-foreground">×{row.quantity.toLocaleString()}</span>
                      ) : null}
                      <span className={cn('w-20 text-right', row.amountCents < 0 && 'text-destructive')}>
                        {formatAmount(row.amountCents)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </CollapsibleContent>
          </div>
        </Collapsible>
      ))}
    </div>
  )
}
